import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Context } from "../../context/Context";
import { Button, CircleX, Input, Select } from "../ui";
import { useModalLayer } from "./modalHooks";
import "./LudConnectFormModal.css";

export default function LudConnectFormModal({
  open,
  title,
  description = "",
  fields = [],
  initialValues = {},
  confirmText,
  cancelText,
  confirmVariant = "primary",
  loading: loadingProp = false,
  onConfirm,
  onClose,
}) {
  const { lang = "ar" } = useContext(Context);
  const ar = lang === "ar";

  const resolvedConfirmText = confirmText || (ar ? "تأكيد" : "Confirm");
  const resolvedCancelText = cancelText || (ar ? "إلغاء" : "Cancel");

  const defaults = useMemo(() => {
    const next = { ...initialValues };

    fields.forEach((field) => {
      if (next[field.name] === undefined) {
        next[field.name] = field.defaultValue ?? "";
      }
    });

    return next;
  }, [fields, initialValues]);

  const [values, setValues] = useState(defaults);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // A ref (not just state) so rapid clicks before the next render are ignored too.
  const submittingRef = useRef(false);
  const loading = loadingProp || submitting;
  const titleId = useId();
  const dialogRef = useRef(null);
  // No dismissal while a request is running (it could still succeed).
  const requestClose = () => {
    if (!loading) onClose?.();
  };

  useModalLayer(open, { containerRef: dialogRef, onEscape: requestClose });

  useEffect(() => {
    if (open) {
      setValues(defaults);
      setError("");
      setSubmitting(false);
    }
  }, [defaults, open]);

  if (!open) return null;

  const submit = async () => {
    const missing = fields.find(
      (field) => field.required && !String(values[field.name] ?? "").trim(),
    );

    if (missing) {
      setError(
        missing.requiredMessage ||
          (ar
            ? `الرجاء إدخال ${missing.label}`
            : `Please enter ${missing.label}`),
      );
      return;
    }

    if (submittingRef.current) return;
    setError("");
    // Disable the form while the request runs (prevents duplicate submits) and
    // show the failure instead of leaving an unhandled rejection.
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onConfirm?.(values);
    } catch (submitError) {
      console.error(submitError);
      setError(
        submitError?.message ||
          (ar ? "تعذّر إرسال الطلب، حاول مرة أخرى" : "The request failed, please try again"),
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`lud-connect-form-modal__backdrop ${
        ar ? "is-rtl" : "is-ltr"
      }`}
      dir={ar ? "rtl" : "ltr"}
      onClick={requestClose}
    >
      <div
        ref={dialogRef}
        className="lud-connect-form-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={loading || undefined}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="lud-connect-form-modal__header">
          <div>
            <h3 id={titleId}>{title}</h3>
            {description && <p>{description}</p>}
          </div>

          <Button
            type="button"
            variant="danger"
            appearance="soft"
            size="sm"
            rounded
            icon={<CircleX size={18} />}
            aria-label={resolvedCancelText}
            title={resolvedCancelText}
            onClick={requestClose}
            disabled={loading}
          />
        </div>

        <div className="lud-connect-form-modal__body">
          {fields.map((field) => {
            if (field.type === "select") {
              return (
                <Select
                  key={field.name}
                  label={field.label}
                  value={values[field.name] ?? ""}
                  options={field.options || []}
                  placeholder={field.placeholder}
                  disabled={loading || field.disabled}
                  fullWidth
                  onChange={(event) =>
                    setValues((current) => ({
                      ...current,
                      [field.name]: event.target.value,
                    }))
                  }
                />
              );
            }

            if (field.type === "textarea") {
              return (
                <label
                  key={field.name}
                  className="lud-connect-form-modal__field"
                >
                  <span>{field.label}</span>

                  <textarea
                    rows={field.rows || 4}
                    value={values[field.name] ?? ""}
                    placeholder={field.placeholder || ""}
                    disabled={loading || field.disabled}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [field.name]: event.target.value,
                      }))
                    }
                  />
                </label>
              );
            }

            return (
              <Input
                key={field.name}
                label={field.label}
                type={field.type || "text"}
                value={values[field.name] ?? ""}
                placeholder={field.placeholder || ""}
                disabled={loading || field.disabled}
                fullWidth
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    [field.name]: event.target.value,
                  }))
                }
              />
            );
          })}

          {error && (
            <p className="lud-connect-form-modal__error" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="lud-connect-form-modal__actions">
          <Button
            type="button"
            variant={confirmVariant}
            appearance="filled"
            onClick={submit}
            disabled={loading}
            loading={submitting}
            fullWidth
          >
            {resolvedConfirmText}
          </Button>

          <Button
            type="button"
            variant="danger"
            appearance="soft"
            onClick={requestClose}
            disabled={loading}
            fullWidth
          >
            {resolvedCancelText}
          </Button>
        </div>
      </div>
    </div>
  );
}
